import { ReactElement } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { LoadingButton as Button } from '@mui/lab';
import { Typography, Divider } from '@mui/material';

import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useEffectOnce } from 'usehooks-ts';

import Form from './components/Form';
import { Event } from './components/Form/types';
import { LoginModalProps, Form as FormType } from './types';
import { schema } from './validation';

import { useSigninMutation, useBalanceMutation, useDepositMutation } from '../../common/hooks';
import {
  Response as BalanceResponse,
  Variables as BalanceVariables
} from '../../common/hooks/useBalanceMutation';
import { Success as SigninSuccess } from '../../common/hooks/useSigninMutation';
import { balanceSuccess } from '../../store/slices/user';
import Heading from '../Heading';
import Modal from '../Modal';
import ModalBody from '../Modal/components/Body';
import ModalFooter from '../Modal/components/Footer';
import ModalHeader from '../Modal/components/Header';
import VStack from '../Stacks/VStack';

export const defaultValues: FormType = {
  fullname: '',
  email: ''
};

export default function LoginModal(props: LoginModalProps): ReactElement {
  const source = axios.CancelToken.source();

  const { open = false, onClose, onSuccess } = props;

  const dispatch = useDispatch();

  const form = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { isDirty } = useFormState({ control: form.control });

  const loginMutation = useSigninMutation({
    options: {
      onSuccess: (response, variables) => {
        const data: SigninSuccess = {
          sessionId: response.sessionId,
          playerId: response.playerId,
          ...variables
        };

        balanceMutation.mutate({
          sessionKey: data.sessionId,
          playerId: data.playerId,
          email: data.email
        });

        if (onSuccess) {
          onSuccess({ ...data });
        }

        onClose();
      }
    }
  });

  const balanceMutation = useBalanceMutation({
    options: {
      onSuccess: (data: BalanceResponse, variables: BalanceVariables) => {
        if (data.cashBalanceInMinorUnits < 1000000) {
          depositMutate.mutate({
            ...variables,
            balance: data.cashBalanceInMinorUnits
          });
        }
      }
    }
  });

  const depositMutate = useDepositMutation({
    options: {
      onSuccess: (data) => {
        dispatch(balanceSuccess(data.cashBalanceInMinorUnits));
      }
    }
  });

  const handleKeypress = (event: Event): void => {
    if ((isDirty && event.code === 'Enter') || event.code === 'NumpadEnter') {
      form.handleSubmit((values) => loginMutation.mutate(values));
    }
  };

  useEffectOnce(() => {
    form.setValue('fullname', localStorage.getItem('user_fullname') || '', { shouldDirty: true });
    form.setValue('email', localStorage.getItem('user_email') || '', { shouldDirty: true });

    return () => {
      source.cancel();

      form.reset({ ...defaultValues });
    };
  });

  return (
    <Modal open={open} onClose={() => onClose()}>
      <ModalHeader renderTitle={(props) => <Typography {...props}>Log in</Typography>} />
      <ModalBody>
        <VStack width='100%' divider={<Divider />} spacing={4} p={2}>
          <Heading
            renderTitle={(props) => (
              <Typography {...props} variant='h4'>
                Welcome Back!
              </Typography>
            )}
            renderSubtitle={(props) => (
              <Typography {...props}>Enter your name and email to play in real mode!</Typography>
            )}
            spacing={0.5}
          />

          <Form form={form} isError={loginMutation.isError} onKeypress={handleKeypress} />
        </VStack>
      </ModalBody>
      <ModalFooter
        renderAction={(props) => (
          <Button
            {...props}
            disabled={!isDirty}
            loading={loginMutation.isLoading}
            onClick={form.handleSubmit((values) => loginMutation.mutate(values))}>
            Log in
          </Button>
        )}
      />
    </Modal>
  );
}
